import React, { forwardRef } from "react";
import { FiTrash2 } from "react-icons/fi";

const SearchableDropdown = forwardRef(({
  label,
  value,
  onChange,
  options,
  searchValue,
  setSearchValue,
  showDropdown,
  setShowDropdown,
  loading,
  onCreate,
  onDelete,
  isCreating,
  placeholder = "Search...",
  createLabel = "Create",
  noResultsText = "No results found",
  inputClassName = "",
  required = false,
  disabled = false,
}, ref) => {

  // ✅ Find selected option for display
  const selectedOption = options?.find(opt => opt._id === value) || 
                         options?.find(opt => opt.id === value);

  const filteredOptions = options?.filter(opt =>
    opt.name?.toLowerCase().includes(searchValue?.toLowerCase() || "")
  ) || [];

  const hasCreateOption = searchValue?.trim() && 
    !options?.some(opt => opt.name?.toLowerCase() === searchValue.toLowerCase());

  return (
    <div className="relative" ref={ref}>
      {/* Label */}
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {/* ✅ Trigger Button — always visible, shows selected value */}
      <button
        type="button"
        onClick={() => !disabled && setShowDropdown(prev => !prev)}
        disabled={disabled}
        className={`w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          disabled ? "opacity-60 cursor-not-allowed bg-gray-50" : "cursor-pointer hover:border-gray-400"
        } ${inputClassName}`}
      >
        <span className={!selectedOption ? "text-gray-400" : "text-gray-800"}>
          {selectedOption?.name || "Select " + label}
        </span>
        <span className="text-gray-400 text-xs ml-2">▼</span>
      </button>

      {/* ✅ Dropdown — only shows when open */}
      {showDropdown && !disabled && (
        <div className="absolute z-20 w-full bg-white border border-gray-300 rounded-lg shadow-lg mt-1 max-h-60 overflow-auto">
          
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder={placeholder}
              className="w-full px-3 py-1.5 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>

          {/* Options */}
          {loading ? (
            <div className="px-4 py-3 text-sm text-gray-500">Loading...</div>
          ) : filteredOptions.length === 0 && !hasCreateOption ? (
            <div className="px-4 py-3 text-sm text-gray-500">{noResultsText}</div>
          ) : (
            <div>
              {filteredOptions.map(opt => {
                const optId = opt._id || opt.id;
                return (
                  <div
                    key={optId}
                    className={`px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm transition-colors flex items-center justify-between group ${
                      (opt._id === value || opt.id === value)
                        ? "bg-blue-100 text-blue-800 font-medium"
                        : "text-gray-700"
                    }`}
                    onClick={() => {
                      onChange(optId);
                      setShowDropdown(false);
                      setSearchValue("");
                    }}
                  >
                    <span className="flex-1 truncate">{opt.name}</span>
                    {onDelete && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(optId, e);
                        }}
                        className="ml-2 p-1 text-red-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                        title={`Delete ${opt.name}`}
                      >
                        <FiTrash2 size={13} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Create Option */}
          {hasCreateOption && onCreate && (
            <div className="border-t border-gray-200 p-2 sticky bottom-0 bg-white">
              <button
                type="button"
                onClick={() => onCreate(searchValue)}
                disabled={isCreating}
                className="w-full px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {isCreating ? "Creating..." : `+ ${createLabel}: "${searchValue}"`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

SearchableDropdown.displayName = "SearchableDropdown";

export default SearchableDropdown;